import React from 'react';
import './DataTable.css';

export interface DataTableColumn<T> {
  /** Unique key identifying the column, used as React key */
  key: string;
  /** Header text displayed in the table header */
  header: React.ReactNode;
  /** Optional custom render function for the cell content */
  render?: (row: T, rowIndex: number) => React.ReactNode;
  /** Optional class name applied to both th and td for this column */
  className?: string;
  /** Optional header click handler (e.g. for sorting) */
  onHeaderClick?: () => void;
}

export interface DataTableProps<T> {
  /** Column definitions */
  columns: DataTableColumn<T>[];
  /** Array of data objects to render as rows */
  data: T[];
  /** Optional CSS class name for page-specific overrides */
  className?: string;
  /** Optional row click handler */
  onRowClick?: (row: T, rowIndex: number) => void;
  /** Optional message shown when data array is empty */
  emptyMessage?: string;
  /** Optional function to return a unique key for each row */
  rowKey?: (row: T, rowIndex: number) => string | number;
  /** Optional function to return additional class names for a row */
  rowClassName?: (row: T, rowIndex: number) => string | undefined;
}

function DataTable<T>({
  columns,
  data,
  className,
  onRowClick,
  emptyMessage = 'No data available.',
  rowKey,
  rowClassName,
}: DataTableProps<T>) {
  return (
    <div className={`data-table-wrapper${className ? ` ${className}` : ''}`}>
      <div className="data-table-scroll-container">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`data-table-th${col.className ? ` ${col.className}` : ''}${col.onHeaderClick ? ' data-table-th--clickable' : ''}`}
                  onClick={col.onHeaderClick}
                  tabIndex={col.onHeaderClick ? 0 : undefined}
                  role={col.onHeaderClick ? 'button' : undefined}
                  onKeyDown={col.onHeaderClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); col.onHeaderClick!(); } } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr className="data-table-empty-row">
                <td colSpan={columns.length} className="data-table-empty-cell">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const key = rowKey ? rowKey(row, rowIndex) : rowIndex;
                const extraClass = rowClassName ? rowClassName(row, rowIndex) : undefined;
                return (
                  <tr
                    key={key}
                    className={`data-table-row${onRowClick ? ' data-table-row--clickable' : ''}${extraClass ? ` ${extraClass}` : ''}`}
                    onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? 'button' : undefined}
                    onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row, rowIndex); } } : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`data-table-td${col.className ? ` ${col.className}` : ''}`}
                      >
                        {col.render
                          ? col.render(row, rowIndex)
                          : (row as Record<string, unknown>)[col.key] != null
                            ? String((row as Record<string, unknown>)[col.key])
                            : ''}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataTable;
